import React, { useCallback, useRef, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import { Sparkles } from 'lucide-react';
import { notify } from '../../../utils/notification';
import useCampaignSpellSync from '../hooks/useCampaignSpellSync';
import { decideFateWindow, syncFateWindows } from '../utils/fateWindowService';
import './RollRequests.scss';

/**
 * Improve Fate (Boon of Fate) on a monster's roll: its attack that hits (before damage) or its successful saving throw
 * against a spell (before the save counts). The sheet offers to subtract 2d4 while the DM waits; "Not now" declines.
 */
export default function FateWindowPrompt({ campaignId, characterId }) {
  const [windows, setWindows] = useState([]), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const lock = useRef(false);
  const load = useCallback(async () => (await syncFateWindows(campaignId, { characterId: String(characterId) })).windows || [], [campaignId, characterId]);
  useCampaignSpellSync({ campaignId: characterId ? campaignId : null, kind: 'fateWindows', load,
    onData: next => setWindows(previous => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next)) });
  const offer = windows[0];
  const decide = async decision => {
    if (!offer || lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const result = await decideFateWindow(campaignId, offer.windowId, { helperId: String(characterId), decision });
      const done = result.window;
      if (decision === 'use' && done.status === 'used' && done.kind === 'save') {
        const outcome = done.saveTotalAfter >= done.dc ? 'still succeeds' : 'fails';
        notify(`Improve Fate: −${done.penalty} (2d4: ${done.rolls.join(', ')}) · ${done.saverName}'s save ${outcome} (${done.saveTotalAfter} vs DC ${done.dc}).`, 'success');
        window.dispatchEvent(new CustomEvent('damage-roll', { detail: { value: done.saveTotalAfter, source: `Improve Fate · ${done.saverName}'s save`,
          breakdown: `−${done.penalty} (2d4: ${done.rolls.join(', ')}) · save ${done.saveTotalAfter} vs DC ${done.dc} · ${outcome === 'fails' ? 'Failure' : 'Success'}` } }));
      } else if (decision === 'use' && done.status === 'used') {
        const outcome = done.attackTotalAfter >= done.armorClass ? 'still hits' : 'misses';
        notify(`Improve Fate: −${done.penalty} (2d4: ${done.rolls.join(', ')}) · ${done.attackerName}'s attack ${outcome} (${done.attackTotalAfter} vs AC ${done.armorClass}).`, 'success');
        window.dispatchEvent(new CustomEvent('damage-roll', { detail: { value: done.attackTotalAfter, source: `Improve Fate · ${done.attackerName}'s attack`,
          breakdown: `−${done.penalty} (2d4: ${done.rolls.join(', ')}) · attack ${done.attackTotalAfter} vs AC ${done.armorClass} · ${outcome === 'misses' ? 'Miss' : 'Hit'}` } }));
      }
      setWindows(previous => previous.filter(item => item.windowId !== offer.windowId));
    } catch (failure) { setError(failure.message); }
    finally { lock.current = false; setBusy(false); }
  };
  if (!offer) return null;
  return <Modal show centered backdrop="static" keyboard={false} className="dnd-modal modern-modal roll-request-prompt" aria-label="Improve Fate">
    <Modal.Body>
      <div className="roll-request__row">
        <span className="roll-request__muted"><Sparkles size={14} aria-hidden="true" /> Improve Fate · Boon of Fate</span>
        <span className="roll-request__pill">{offer.kind === 'save' ? `DC ${offer.dc}` : `AC ${offer.armorClass}`}</span>
      </div>
      <div className="roll-request__title">{offer.kind === 'save' ? `${offer.saverName} resists${offer.spellName ? ` ${offer.spellName}` : ''}` : `${offer.attackerName} hits ${offer.targetName}`}</div>
      <div className="roll-request__muted">{offer.kind === 'save'
        ? `${offer.ability ? `${offer.ability[0].toUpperCase()}${offer.ability.slice(1)} save` : 'Save'} ${offer.saveTotal} vs DC ${offer.dc}.`
        : `Attack roll ${offer.attackTotal} vs AC ${offer.armorClass}.`} Subtract 2d4 from it? It returns when you roll Initiative or finish a rest.</div>
      {error && <Alert variant="danger" className="mt-2 mb-0 py-1 small">{error}</Alert>}
      <div className="roll-request__row mt-2">
        <Button size="sm" variant="outline-light" disabled={busy} onClick={() => decide('skip')}>Not now</Button>
        <Button size="sm" disabled={busy} onClick={() => decide('use')}>{busy ? 'Rolling…' : 'Subtract 2d4'}</Button>
      </div>
    </Modal.Body>
  </Modal>;
}
